import React, {useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import styled from 'styled-components';
import {Button, EMPHASIS, Heading, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import MissionControlHeader from '../type-script-files/MissionControlHeader';
import Util from '../../../forecast-app/shared/util/util';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import StartFlyPreview from '../../../mutations/mission_control/StartFlyPreviewMutation';
import StopFlyPreview from '../../../mutations/mission_control/StopFlyPreviewMutation';
import type {FlyPreviewsPage_viewer$key} from './__generated__/FlyPreviewsPage_viewer.graphql';
import type {MissionControlHeader_viewer$key} from '../type-script-files/__generated__/MissionControlHeader_viewer.graphql';
import {
	describeLastUsed,
	FlyPreviewRow,
	needsStopConfirm,
	NOT_CONFIGURED_MESSAGE,
	sortRows,
	withMember,
} from './flyPreviewRows';
import {FlyPreviewTableRow} from './FlyPreviewTableRow';

export const flyPreviewsPageQuery = graphql`
	query FlyPreviewsPage_Query {
		viewer {
			actualPersonId
			component(name: "control_fly_previews")
			...FlyPreviewsPage_viewer
		}
	}
`;

const flyPreviewsPageFragment = graphql`
	fragment FlyPreviewsPage_viewer on Viewer {
		...MissionControlHeader_viewer
		missionControlData {
			id
			flyPreviews {
				id
				slug
				branchName
				webUrl
				status
				machinesTotal
				machinesRunning
				lastUsedAt
				lastUsedKnown
				createdAt
				deployedAt
			}
		}
	}
`;

// The palette's decorative border, the same one the feature flags page's cards and tables use for a
// hairline rule — see featureFlagSurface.ts's FEATURE_FLAG_CARD_BORDER for the same derivation.
const FLY_PREVIEW_TABLE_BORDER = tokensForThemeProvider.sky.sys.color.border.decorative;

const PageBody = styled.div`
	padding: 24px;
	max-width: 1400px;
`;

const Toolbar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
`;

const PreviewTable = styled.table`
	width: 100%;
	border-collapse: collapse;

	th,
	td {
		text-align: left;
		padding: 8px 12px;
		border-bottom: 1px solid ${FLY_PREVIEW_TABLE_BORDER};
		vertical-align: middle;
		/* Every column but the branch name sizes to its content on one line; the branch name
		   takes the remaining width and is the only one that may wrap. */
		white-space: nowrap;
	}

	th:first-child,
	td:first-child {
		white-space: normal;
		width: 100%;
	}
`;

interface FlyPreviewsPageProps {
	viewer: FlyPreviewsPage_viewer$key;
}

export default function FlyPreviewsPage({viewer}: FlyPreviewsPageProps): JSX.Element {
	const data = useFragment(flyPreviewsPageFragment, viewer);
	// Per row, so one row's mutation finishing never re-enables another row's buttons.
	const [busySlugs, setBusySlugs] = useState<ReadonlySet<string>>(() => new Set());
	const [stoppingSlugs, setStoppingSlugs] = useState<ReadonlySet<string>>(() => new Set());
	// Refetching the page query into the Mission Control store re-renders this fragment in place.
	const {fetch: refetch, loading: refreshing} = useForecastFetchQuery(flyPreviewsPageQuery, false, true);

	// Assigning to the header's fragment key keeps `...MissionControlHeader_viewer` type-checked; the
	// header's props are typed as its resolved data, hence the cast (same as FeatureFlagsPage).
	const headerViewer: MissionControlHeader_viewer$key = data;
	const header = (
		<MissionControlHeader viewer={headerViewer as unknown as React.ComponentProps<typeof MissionControlHeader>['viewer']} />
	);

	const previews = data.missionControlData?.flyPreviews;
	if (previews == null) {
		return (
			<>
				{header}
				<PageBody>
					<Heading size="3">Fly previews</Heading>
					<Text>{NOT_CONFIGURED_MESSAGE}</Text>
				</PageBody>
			</>
		);
	}

	const setBusy = (slug: string, busy: boolean) => setBusySlugs(current => withMember(current, slug, busy));
	const setStopping = (slug: string, stopping: boolean) => setStoppingSlugs(current => withMember(current, slug, stopping));

	// Util.CommitMutation's fallback shows the standard error modal; it returns true when it has
	// scheduled a retry, in which case the row should stay busy.
	const onError = (slug: string) => (transaction: unknown, onFailure: (transaction: unknown) => boolean | void) => {
		if (!onFailure(transaction)) {
			setBusy(slug, false);
			setStopping(slug, false);
		}
	};

	const start = (row: FlyPreviewRow) => {
		setBusy(row.slug, true);
		setStopping(row.slug, false);
		Util.CommitMutation(StartFlyPreview, {slug: row.slug}, () => setBusy(row.slug, false), true, null, onError(row.slug));
	};

	const stop = (row: FlyPreviewRow) => {
		const now = Date.now();
		const question = row.lastUsedKnown
			? `Someone used this preview ${describeLastUsed(row, now)}. Stop it anyway?`
			: "Usage for this preview couldn't be checked. Stop it anyway?";
		if (needsStopConfirm(row, now) && !window.confirm(question)) {
			return;
		}
		setBusy(row.slug, true);
		// fly stops machines asynchronously, so the mutation's answer usually still reads RUNNING; the
		// row shows "Stopping…" and polls until the stack reads STOPPED.
		setStopping(row.slug, true);
		Util.CommitMutation(StopFlyPreview, {slug: row.slug}, () => setBusy(row.slug, false), true, null, onError(row.slug));
	};

	return (
		<>
			{header}
			<PageBody>
				<Toolbar>
					<Heading size="3">Fly previews</Heading>
					<Button emphasis={EMPHASIS.MEDIUM} icon="reload" disabled={refreshing} onPress={() => refetch({})}>
						Refresh
					</Button>
				</Toolbar>
				<PreviewTable>
					<thead>
						<tr>
							<th>Preview</th>
							<th>Status</th>
							<th>Last used</th>
							<th>Created</th>
							<th>Deployed</th>
							<th>Link</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{sortRows(previews).map(row => (
							<FlyPreviewTableRow
								key={row.id}
								row={row}
								busy={busySlugs.has(row.slug)}
								stopping={stoppingSlugs.has(row.slug)}
								onStart={start}
								onStop={stop}
								onStopSettled={slug => setStopping(slug, false)}
							/>
						))}
					</tbody>
				</PreviewTable>
			</PageBody>
		</>
	);
}
